import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, CheckCircle2, Package, Globe, Mail, Phone, User, Building, Send, Layers, BoxIcon, Thermometer, FileText, ClipboardList, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { products, getWhatsAppLink } from '../mock';

const ProductDetail = () => {
  const { id } = useParams();
  const product = products.find((p) => p.id === parseInt(id));

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', country: '', quantity: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/product-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: product.id, productName: product.name, ...formData, recaptchaToken: 'disabled'
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success("Inquiry Submitted Successfully!", {
          description: `Thank you for your interest in ${product.name}. We'll contact you within 24 hours.`,
        });
        setFormData({ name: '', email: '', phone: '', company: '', country: '', quantity: '', message: '' });
      } else {
        throw new Error(data.detail || 'Failed to submit inquiry');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error("Failed to Submit", {
        description: "Please try again or contact us directly at info@agrocorpindia.com",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <Link to="/products">
            <Button className="bg-green-700 hover:bg-green-800 text-white">
              <ArrowLeft className="mr-2 w-4 h-4" /> Back to Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const seo = product.seoMeta || {};
  const whatsappLink = getWhatsAppLink(product.name);

  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
        <title>{seo.title || `${product.name} – Export Quality | Agro Corp India`}</title>
        <meta name="description" content={seo.description || product.fullDescription.substring(0, 160)} />
        <meta name="keywords" content={seo.keywords || `${product.name} exporter India, wholesale ${product.name}`} />
        <link rel="canonical" href={`https://www.agrocorpindia.com/products/${product.id}`} />
        <meta property="og:title" content={seo.title || `${product.name} – Agro Corp India`} />
        <meta property="og:description" content={seo.description || product.fullDescription.substring(0, 160)} />
        <meta property="og:image" content={product.image} />
        <meta property="og:type" content="product" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "Product",
          "name": product.name, "description": product.fullDescription,
          "image": product.image,
          "brand": { "@type": "Brand", "name": "Agro Corp India" },
          "manufacturer": { "@type": "Organization", "name": "Agro Corp India" },
          "category": product.category,
          ...(product.hsnCode ? { "productID": product.hsnCode } : {})
        })}</script>
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-green-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-600 hover:text-green-700">Home</Link>
            <span className="text-gray-400">/</span>
            <Link to="/products" className="text-gray-600 hover:text-green-700">Products</Link>
            <span className="text-gray-400">/</span>
            <span className="text-green-700 font-semibold">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Details */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img src={product.image} alt={product.name} className="w-full h-[500px] object-cover" />
              <div className="absolute top-4 right-4 bg-amber-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                Export Quality
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider bg-amber-50 px-4 py-2 rounded-full inline-block mb-4">
                  {product.category}
                </span>
                <h1 className="text-4xl md:text-5xl font-bold text-green-900 mb-4" data-testid="product-name">
                  {product.name}
                </h1>
                {product.hsnCode && (
                  <p className="text-sm text-gray-500 mb-4" data-testid="product-hsn">HSN Code: {product.hsnCode}</p>
                )}
                <p className="text-lg text-gray-700 leading-relaxed">{product.fullDescription}</p>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-green-900 mb-4">Key Features</h3>
                <div className="space-y-3">
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-green-900 mb-4 flex items-center">
                  <Globe className="w-6 h-6 mr-2 text-amber-600" />
                  Available for Worldwide Export
                </h3>
                <p className="text-gray-600">We export to international markets across all continents. Contact us to discuss shipment to your destination.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button data-testid="whatsapp-inquiry-btn" className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300">
                    <MessageCircle className="mr-2 w-5 h-5" />
                    Inquire on WhatsApp
                  </Button>
                </a>
                <Button
                  data-testid="request-bulk-quote-btn"
                  onClick={() => document.getElementById('inquiry-form').scrollIntoView({ behavior: 'smooth' })}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Request Bulk Quote
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="py-12 bg-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-green-900 mb-8 flex items-center">
            <Package className="w-8 h-8 mr-3 text-amber-600" />
            Product Specifications
          </h2>
          <Card>
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(product.specifications).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-center border-b border-gray-200 pb-4">
                    <span className="font-semibold text-gray-700">{key}:</span>
                    <span className="text-gray-900">{value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Applications */}
      {product.applications && product.applications.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-green-900 mb-8 flex items-center" data-testid="applications-heading">
              <Layers className="w-8 h-8 mr-3 text-amber-600" />
              Applications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {product.applications.map((app, index) => (
                <div key={index} className="flex items-start space-x-3 bg-green-50 rounded-xl p-4 border border-green-100" data-testid={`application-item-${index}`}>
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{app}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Packaging, Shelf Life & Storage */}
      {(product.packagingDetails || product.shelfLife || product.storageInstructions) && (
        <section className="py-12 bg-green-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {product.packagingDetails && (
                <Card className="border-2 border-green-100" data-testid="packaging-details-card">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center">
                      <BoxIcon className="w-6 h-6 mr-2 text-amber-600" /> Packaging Details
                    </h3>
                    <ul className="space-y-3">
                      {product.packagingDetails.map((item, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                          <span className="text-gray-700 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
              {(product.shelfLife || product.storageInstructions) && (
                <Card className="border-2 border-green-100" data-testid="storage-info-card">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center">
                      <Thermometer className="w-6 h-6 mr-2 text-amber-600" /> Shelf Life & Storage
                    </h3>
                    {product.shelfLife && (
                      <div className="mb-4 bg-amber-50 rounded-lg p-3 border border-amber-200">
                        <p className="text-sm font-semibold text-amber-800">Shelf Life</p>
                        <p className="text-gray-700 text-sm">{product.shelfLife}</p>
                      </div>
                    )}
                    {product.storageInstructions && (
                      <ul className="space-y-2">
                        {product.storageInstructions.map((instruction, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-sm">{instruction}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              )}
              {product.exportDocumentation && (
                <Card className="border-2 border-green-100" data-testid="export-docs-card">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center">
                      <FileText className="w-6 h-6 mr-2 text-amber-600" /> Export Documentation
                    </h3>
                    <ul className="space-y-2">
                      {product.exportDocumentation.map((doc, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <ClipboardList className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                          <span className="text-gray-700 text-sm">{doc}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Inquiry Form */}
      <section id="inquiry-form" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-green-900 mb-4">
              Request a Bulk Quote for {product.name}
            </h2>
            <p className="text-lg text-gray-600">
              Fill out the form below and our export team will get back to you within 24 hours
            </p>
          </div>
          <Card className="border-2 border-green-200 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="product-inquiry-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2"><User className="inline w-4 h-4 mr-1" />Full Name *</label>
                    <Input type="text" name="name" value={formData.name} onChange={handleChange} required data-testid="inquiry-name-input" placeholder="Enter your name" className="w-full border-2 border-gray-200 focus:border-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2"><Mail className="inline w-4 h-4 mr-1" />Email Address *</label>
                    <Input type="email" name="email" value={formData.email} onChange={handleChange} required data-testid="inquiry-email-input" placeholder="your@email.com" className="w-full border-2 border-gray-200 focus:border-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2"><Phone className="inline w-4 h-4 mr-1" />Phone Number *</label>
                    <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} required data-testid="inquiry-phone-input" placeholder="+1 234 567 8900" className="w-full border-2 border-gray-200 focus:border-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2"><Building className="inline w-4 h-4 mr-1" />Company Name</label>
                    <Input type="text" name="company" value={formData.company} onChange={handleChange} data-testid="inquiry-company-input" placeholder="Your company name" className="w-full border-2 border-gray-200 focus:border-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2"><Globe className="inline w-4 h-4 mr-1" />Country *</label>
                    <Input type="text" name="country" value={formData.country} onChange={handleChange} required data-testid="inquiry-country-input" placeholder="Your country" className="w-full border-2 border-gray-200 focus:border-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2"><Package className="inline w-4 h-4 mr-1" />Required Quantity</label>
                    <Input type="text" name="quantity" value={formData.quantity} onChange={handleChange} data-testid="inquiry-quantity-input" placeholder="e.g., 1000 kg" className="w-full border-2 border-gray-200 focus:border-green-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Requirements / Message</label>
                  <Textarea name="message" value={formData.message} onChange={handleChange} rows={5} data-testid="inquiry-message-input" placeholder="Tell us more about your bulk requirements..." className="w-full border-2 border-gray-200 focus:border-green-500" />
                </div>
                <Button type="submit" disabled={isSubmitting} data-testid="submit-inquiry-btn" className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-4 text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSubmitting ? 'Submitting...' : (<>Request Bulk Quote <Send className="ml-2 w-5 h-5" /></>)}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Related Products */}
      <section className="py-20 bg-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-green-900 mb-8 text-center">Other Products You May Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {products.filter((p) => p.id !== product.id).slice(0, 3).map((relatedProduct) => (
              <Card key={relatedProduct.id} className="group overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="relative overflow-hidden">
                  <img src={relatedProduct.image} alt={relatedProduct.name} className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-green-900 mb-2">{relatedProduct.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{relatedProduct.shortDescription}</p>
                  <Link to={`/products/${relatedProduct.id}`}>
                    <Button className="w-full bg-green-700 hover:bg-green-800 text-white">Inquire Now</Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
